import storage from '../config/appStorage';
import i18n from '../utils/i18n';
import {
  ACTIVE_READING_PLAN,
  DEFAULT_THEME_SETTINGS,
  STORAGE_LANGUAGE,
  settingsJson,
  userId,
} from '../constants/stringsAndFields';
import {getUserSettingsRow, upsertUserSettings} from '../database/userSettings';
import {
  getThemeSettings,
  refreshThemeFromStorage,
  replaceThemeSettingsSilent,
} from '../utils/storage';

/**
 * Builds a JSON blob compatible with Supabase `user_settings.settings` and local `UserSettings.settingsJson`.
 */
export async function buildSettingsPayloadFromAsyncStorage() {
  const theme = await getThemeSettings();
  const language = await storage.getItem(STORAGE_LANGUAGE);
  const activeReadingPlan = await storage.getItem(ACTIVE_READING_PLAN);
  return {
    theme,
    language: language || null,
    activeReadingPlan: activeReadingPlan || null,
  };
}

export async function migrateAsyncStorageToUserSettingsDb(db, uid) {
  const payload = await buildSettingsPayloadFromAsyncStorage();
  await upsertUserSettings(db, {
    [userId]: uid,
    [settingsJson]: JSON.stringify(payload),
  });
}

/**
 * After `syncUserData` pulls `user_settings` into SQLite, copy them to AsyncStorage
 * and refresh theme / i18n so the UI matches the downloaded state.
 */
export async function applyUserSettingsFromDbToApp(db, uid) {
  if (!db || !uid) {
    return;
  }
  const row = await getUserSettingsRow(db, uid);
  if (!row?.[settingsJson]) {
    return;
  }
  let parsed;
  try {
    parsed = JSON.parse(row[settingsJson]);
  } catch {
    return;
  }
  let themeUpdated = false;
  if (typeof parsed.theme === 'object' && parsed.theme !== null) {
    const merged = {...DEFAULT_THEME_SETTINGS, ...parsed.theme};
    await replaceThemeSettingsSilent(merged);
    themeUpdated = true;
  }
  if (
    Object.prototype.hasOwnProperty.call(parsed, 'language') &&
    typeof parsed.language === 'string' &&
    parsed.language.length
  ) {
    await storage.setItem(STORAGE_LANGUAGE, parsed.language);
    await i18n.changeLanguage(parsed.language);
  }
  if (Object.prototype.hasOwnProperty.call(parsed, 'activeReadingPlan')) {
    if (parsed.activeReadingPlan) {
      await storage.setItem(ACTIVE_READING_PLAN, parsed.activeReadingPlan);
    } else {
      await storage.removeItem(ACTIVE_READING_PLAN);
    }
  }
  if (themeUpdated) {
    await refreshThemeFromStorage();
  }
}
